import React from 'react';

import { withEventScope } from '@repo/event-processor';
import {
  SubscriptionPlanType,
  type Subscription,
} from '@repo/subscription/api';
import { Spacer } from '@repo/ui/components/Spacer';

import {
  usePlanSelectionStore,
  planSelectionStoreSelector,
} from '../../../../stores/planSelectionStore';
import usePlanIntervalData from '../hooks/usePlanIntervalData';

import { PlanVariations } from './styles';
import Variation from './Variation/Variation';

/**
 * This component allows the user to select between paying yearly or monthly for a subscription plan.
 * It displays the available payment intervals and highlights the active selection.
 *
 * @param {Object} props - The properties for the component.
 * @param {Subscription | null} props.currentSubscription - The current subscription of the user, if any.
 * @param {boolean} [props.isStacked] - Indicates whether to display the intervals in a stacked layout.
 * @returns {JSX.Element | null} - The rendered PlanIntervalSelector component or null if the user is upgrading to the EXPERT plan.
 */
const PlanIntervalSelector: React.FC<{
  currentSubscription: Subscription | null;
  isStacked?: boolean;
}> = ({ currentSubscription, isStacked }) => {
  const plan = usePlanSelectionStore(planSelectionStoreSelector.plan);
  const yearly = usePlanSelectionStore(planSelectionStoreSelector.yearly);
  const { setYearly } = usePlanSelectionStore(
    planSelectionStoreSelector.actions
  );
  const upgrade = usePlanSelectionStore(planSelectionStoreSelector.upgrade);
  const disabled = usePlanSelectionStore(planSelectionStoreSelector.disabled);

  const planIntervalData = usePlanIntervalData({ plan });

  // If the user is upgrading to the EXPERT plan, do not render the component
  if (
    upgrade &&
    currentSubscription?.plan?.name === SubscriptionPlanType.EXPERT
  ) {
    return null;
  }

  return (
    <>
      <Spacer height="24px" />
      <PlanVariations isStacked={isStacked}>
        <Variation
          disabled={disabled}
          isActive={yearly}
          label="Pay yearly"
          {...planIntervalData.yearly}
          subLabel={`SAVE ${planIntervalData.yearlySave}`}
          onClick={(): void => setYearly(true)}
        />
        <Variation
          disabled={disabled}
          isActive={!yearly}
          label="Pay monthly"
          {...planIntervalData.monthly}
          onClick={(): void => setYearly(false)}
        />
      </PlanVariations>
    </>
  );
};

export default withEventScope(PlanIntervalSelector, {
  module: 'planIntervalSelector',
});
